import { useState } from "react";
import { UploadCloud, Camera, Smartphone, Mail, Inbox, ArrowRightLeft, Printer, Mic } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { faxStatus } from "../lib/api";
import { PhoneScanDialog } from "./PhoneScanDialog";
import { GmailDialog } from "./GmailDialog";
import { MigrateWizard } from "./MigrateWizard";
import { tr } from "../lib/i18n";

const TILES = [
  { id: "upload", Icon: UploadCloud, title: "Upload files", text: "PDFs, photos, scans — drop in as many as you like." },
  { id: "camera", Icon: Camera, title: "Scan with this camera", text: "Snap a receipt or letter; we straighten and file it." },
  { id: "record", Icon: Mic, title: "Record a letter", text: "A voice or video message for your family — transcribed and kept safe." },
  { id: "phone", Icon: Smartphone, title: "Scan from your phone", text: "Point your phone at a QR code and use it as the scanner." },
  { id: "email", Icon: Mail, title: "Email or text it in", text: "Forward bills and statements to your vault address, or text a photo." },
  { id: "fax", Icon: Printer, title: "Fax it in", text: "Clinics, county offices and relatives can fax straight into the vault." },
  { id: "gmail", Icon: Inbox, title: "Google auto-import", text: "Connect Gmail & Drive once; statements, receipts and scans file themselves." },
  { id: "migrate", Icon: ArrowRightLeft, title: "Switch from another app", text: "Trustworthy, Everplans, a Drive or Dropbox folder — bring it all over in one go." },
];

export const CapturePanel = ({ onUpload, onCamera, onRecord, onForward, phoneOpen, setPhoneOpen, gmailOpen, setGmailOpen, migrateOpen, setMigrateOpen, migrateSource = "", migrateResumeJob = null, onImported }) => {
  const [localPhone, setLocalPhone] = useState(false);
  const [localGmail, setLocalGmail] = useState(false);
  const [localMigrate, setLocalMigrate] = useState(false);
  const phone = phoneOpen ?? localPhone;
  const setPhone = setPhoneOpen || setLocalPhone;
  const gmail = gmailOpen ?? localGmail;
  const setGmail = setGmailOpen || setLocalGmail;
  const migrate = migrateOpen ?? localMigrate;
  const setMigrate = setMigrateOpen || setLocalMigrate;
  const act = { upload: onUpload, camera: onCamera, record: onRecord, phone: () => setPhone(true), email: onForward, fax: onForward, gmail: () => setGmail(true), migrate: () => setMigrate(true) };
  const { data: fax } = useQuery({ queryKey: ["fax-status"], queryFn: faxStatus, retry: false, staleTime: 5 * 60 * 1000 });
  const tileText = (id, text) => (id === "fax" && fax?.number_pretty ? `Your household fax: ${fax.number_pretty}` : text);
  return (
    <section className="mb-6" data-testid="capture-panel">
      <div className="text-[10px] uppercase tracking-widest font-mono text-primary mb-2">{tr("Add documents")}</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {TILES.map(({ id, Icon, title, text }) => (
          <button key={id} type="button" data-testid={`capture-${id}-btn`} onClick={act[id]} className="group text-left rounded-2xl border border-border bg-card p-3 sm:p-4 flex items-center gap-3 sm:block min-h-[56px] hover:border-primary hover:-translate-y-0.5 transition-[border-color,transform] duration-200">
            <span className="grid place-items-center h-9 w-9 shrink-0 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors"><Icon className="h-[18px] w-[18px]" /></span>
            <div className="sm:mt-3 text-sm font-semibold leading-tight">{tr(title)}</div>
            <div className="mt-0.5 text-xs text-muted-foreground leading-relaxed hidden sm:block" data-testid={`capture-${id}-text`}>{tr(tileText(id, text))}</div>
          </button>
        ))}
      </div>
      <PhoneScanDialog open={phone} onClose={() => setPhone(false)} />
      <GmailDialog open={gmail} onClose={() => setGmail(false)} onImported={onImported} />
      <MigrateWizard open={migrate} onClose={() => setMigrate(false)} onImported={onImported} initialSource={migrateSource} resumeJobId={migrateResumeJob} />
    </section>
  );
};
